// The Ferry Board (Oct 27, programming-plan.md Phase E; config/stops.json `ferries`): departures from the Ferry
// Building, several per route (Jared: "list several departure times for each route"). Golden Gate Ferry on one page,
// SF Bay Ferry on the other, from 511's timetables, with SF Bay Ferry's live times marking a late boat. Blue & Gold
// (Pier 41) isn't in 511, and the footer says so.
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import { asOf } from './format.mjs';

const { stops, clock } = window.RHL;
const ferries = stops.ferries ?? { lines: [], perRoute: 3 };

const local = (date) => {
	const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(date).map((p) => [p.type, p.value]));
	return { hhmm: `${parts.hour}:${parts.minute}`, day: parts.weekday };
};
// the timetable's day type for a weekday name, most specific first
const dayTypes = (day) => {
	if (day === 'Sat') return ['saturday', 'weekend'];
	if (day === 'Sun') return ['sunday', 'weekend'];
	return ['weekday', 'weekdays'];
};
// a day type is named by its word anywhere in the timetable's name (SF Bay Ferry: "year round starting 3/9/2026
// (weekday)"; Golden Gate Ferry: "weekday")
const timesFor = (schedule, day) => {
	const entries = Object.entries(schedule?.times ?? {});
	return dayTypes(day).map((word) => entries.find(([name, times]) => name.includes(word) && times.length)?.[1]).find(Boolean) ?? [];
};
// "07:40" → "7:40a"
const clockText = (hhmm) => {
	const [h, m] = hhmm.split(':').map(Number);
	return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h < 12 ? 'a' : 'p'}`;
};

// the next departures of a line today, each with minutes late when the live feed has it; else tomorrow's first
const departures = (line, now) => {
	const schedule = (data('ferrySchedule') ?? []).find((s) => s.id === line.id);
	if (!schedule) return null;
	const today = local(now);
	const next = timesFor(schedule, today.day).filter((t) => t > today.hhmm).slice(0, ferries.perRoute);
	const live = (data('ferries') ?? []).filter((d) => d.line === line.id && d.expected);
	const late = (hhmm) => {
		const match = live.find((d) => local(new Date(d.aimed)).hhmm === hhmm);
		const minutes = match ? Math.round((new Date(match.expected) - new Date(match.aimed)) / 60000) : 0;
		return minutes >= 3 ? minutes : 0;
	};
	if (next.length) return next.map((t) => ({ t, late: late(t) }));
	const tomorrow = local(new Date(now.getTime() + 86400000));
	const first = timesFor(schedule, tomorrow.day)[0];
	return first ? [{ t: first, tomorrow: true }] : [];
};

const row = (line, now) => {
	const list = departures(line, now);
	if (!list) return '';
	const when = list.length
		? list.map((d) => (d.tomorrow ? `<span class="dim">tomorrow ${clockText(d.t)}</span>` : `${clockText(d.t)}${d.late ? ` <span class="warn">+${d.late}</span>` : ''}`)).join(' &nbsp;')
		: '<span class="dim">none today</span>';
	return `<div class="row"><div>${escapeHtml(line.name)}</div><div class="v">${when}</div></div>`;
};

class FerryBoard extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Ferries');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return (data('ferrySchedule') ?? []).length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const foot = `<div class="asof">511.org &middot; Blue &amp; Gold (Pier 41) not included &middot; ${asOf(fetchedAt('ferrySchedule'), now)}</div>`;
		return [...new Set(ferries.lines.map((l) => l.agency))].map((agency) => {
			const rows = ferries.lines.filter((l) => l.agency === agency).map((l) => row(l, now)).filter(Boolean);
			return rows.length ? {
				seconds: 12,
				title: ['Ferries', 'Departing'],
				html: `<div class="hdr">${escapeHtml(agency)} &middot; from the Ferry Building</div>
					<div class="list ferries">${rows.join('')}</div>${foot}`,
			} : null;
		}).filter(Boolean);
	}
}

const display = new FerryBoard(33, 'ferry-board');
display.makeLoopScreen();
registerDisplay(display);
